import type { GatewayStatus, SaveGateway } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Field, PasswordInput, Select, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, PlugZap } from 'lucide-react';
import { useState } from 'react';
import { fieldErrors } from '../clinical/patients-page.js';
import { api } from '../lib/api.js';
import { SectionHeader } from './layout.js';

const gatewayQuery = queryOptions({ queryKey: ['billing', 'gateway'], queryFn: () => api<GatewayStatus>('/billing/gateway') });

/** Configurações › Pagamentos: conta do Asaas da clínica (o dinheiro cai direto nela). */
export function PaymentsSettingsPage() {
  const qc = useQueryClient();
  const { data } = useQuery(gatewayQuery);
  const [env, setEnv] = useState<SaveGateway['environment']>('sandbox');
  const [key, setKey] = useState('');
  const save = useMutation({
    mutationFn: () => api<GatewayStatus>('/billing/gateway', { method: 'PUT', body: { environment: env, apiKey: key } }),
    onSuccess: (n) => { qc.setQueryData(gatewayQuery.queryKey, n); setKey(''); },
  });
  const errs = fieldErrors(save.error);
  if (!data) return <Spinner className="size-5 text-fg-subtle" label="Carregando" />;
  return (
    <div className="grid gap-6">
      <SectionHeader title="Pagamentos (Asaas)" description="Cobranças por Pix, cartão e boleto com a conta Asaas da própria clínica. A baixa é automática pelo aviso (webhook) do Asaas." />
      {data.configured && (
        <Card className="flex flex-wrap items-center gap-3 p-5">
          <CheckCircle2 className="size-5 text-success" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-fg">Conectado{data.accountName ? ` · ${data.accountName}` : ''}</p>
            <p className="text-[13px] text-fg-muted">Chave terminada em {data.apiKeyLast4}{data.updatedAt ? ` · salva em ${new Date(data.updatedAt).toLocaleDateString('pt-BR')}` : ''}</p>
          </div>
          <Badge tone={data.environment === 'production' ? 'success' : 'warning'}>{data.environment === 'production' ? 'Produção' : 'Teste (sandbox)'}</Badge>
          <Badge tone={data.webhook ? 'success' : 'danger'}>{data.webhook ? 'Webhook ativo' : 'Webhook não cadastrado'}</Badge>
        </Card>
      )}
      {data.configured && !data.webhook && <Alert tone="warning" title="Sem aviso automático">O Asaas não aceitou o cadastro do webhook. Salve a chave de novo; se continuar, confira a chave no painel do Asaas.</Alert>}
      <Card>
        <CardHeader title={data.configured ? 'Trocar a chave' : 'Conectar a conta'} description="No Asaas: Integrações › Chaves de API › Gerar chave. Comece pelo ambiente de teste (sandbox), com a chave da conta de testes." />
        <form className="grid gap-4 p-5 sm:grid-cols-[200px_1fr_auto] sm:items-end" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
          <Field label="Ambiente">
            <Select value={env} onChange={(e) => setEnv(e.target.value as SaveGateway['environment'])}>
              <option value="sandbox">Teste (sandbox)</option>
              <option value="production">Produção</option>
            </Select>
          </Field>
          <Field label="Chave de API" error={errs.apiKey} hint="Fica cifrada; ninguém da equipe consegue ver depois.">
            <PasswordInput value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" placeholder="$aact_…" />
          </Field>
          <Button type="submit" disabled={key.trim().length < 20} loading={save.isPending}><PlugZap className="size-4" />Conectar</Button>
          {save.error && !errs.apiKey && <div className="sm:col-span-3"><Alert tone="danger">{save.error.message}</Alert></div>}
        </form>
      </Card>
    </div>
  );
}
